import React, { useState, useRef, useEffect, useMemo } from "react";
import { db } from "../../Hooks/FirebaseConfig";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import Cookies from 'js-cookie';
import './chat.css';
import { useDispatch, useSelector } from "react-redux";
import { MemberList } from "../../apis/api/DirectoryApi";

// ── Unread helpers using localStorage ──
const LS_KEY = (userId) => `chat_read_${userId}`;

const getReadTimes = (userId) => {
  try { return JSON.parse(localStorage.getItem(LS_KEY(userId)) || '{}'); }
  catch { return {}; }
};

const markRead = (userId, peerId) => {
  const times = getReadTimes(userId);
  times[String(peerId)] = Date.now() / 1000;
  localStorage.setItem(LS_KEY(userId), JSON.stringify(times));
};

const ChatSidebar = ({ sendData, sidebarVersion }) => {
  const dispatch = useDispatch();

  const [ChatUsers, setChatUsers] = useState([]);
  const [Userslist, setUsersList] = useState([]);
  const [chatSearch, setChatSearch] = useState('');

  // New-chat modal state
  const [showModal, setShowModal] = useState(false);
  const [modalSearch, setModalSearch] = useState('');

  const currentUserId = Cookies.get('userID');
  const { MemberData } = useSelector((state) => state.DirectorySlice);
  const loadedUserIds = useRef(new Set());
  const modalInputRef = useRef(null);
  const memberDataRef = useRef({});

  const user = JSON.parse(Cookies.get('user'));

  useEffect(() => {
    if (MemberData) {
      // Build a lookup map by member id for fast merging
      const map = {};
      MemberData.forEach((m) => { map[String(m.id)] = m; });
      memberDataRef.current = map;
      setUsersList(MemberData);
      // Patch any already-loaded ChatUsers that are missing name/logo
      setChatUsers((prev) =>
        prev.map((u) => {
          const api = map[String(u.id)];
          if (!api) return u;
          return {
            ...u,
            full_name: api.full_name || u.full_name || '',
            company_name: api.company_name || u.company_name || '',
            member_logo: api.member_logo || u.member_logo || null,
          };
        })
      );
    }
  }, [MemberData]);

  const delay = ms => new Promise(res => setTimeout(res, ms));

  const fetchOneProfile = (id) =>
    getDoc(doc(db, "users", id))
      .then((userDoc) => {
        if (userDoc.exists()) return userDoc;
        return delay(2000).then(() => getDoc(doc(db, "users", id.toString())));
      })
      .then((userDoc) => {
        if (!loadedUserIds.current.has(id)) {
          loadedUserIds.current.add(id);
          // Merge Firebase data with API MemberData — API fields take priority for name/logo
          const firebaseData = userDoc.exists() ? userDoc.data() : {};
          const apiData = memberDataRef.current[id] || memberDataRef.current[String(id)] || {};
          return {
            id,
            ...firebaseData,
            full_name: apiData.full_name || firebaseData.full_name || '',
            company_name: apiData.company_name || firebaseData.company_name || '',
            member_logo: apiData.member_logo || firebaseData.member_logo || null,
          };
        }
        return null;
      });

  const getUserProfiles = (userIds) =>
    Promise.all(
      userIds
        .filter((id) => !loadedUserIds.current.has(id))
        .map((id) => fetchOneProfile(id))
    ).then((results) => results.filter(Boolean));

  const processSnapshots = (snapshot) => {
    const userIds = new Set();
    const latestMsg = {};   // peerId -> { text, ts, mine }
    const unreadCount = {}; // peerId -> number of unread messages from them

    const readTimes = getReadTimes(currentUserId);

    snapshot.forEach((d) => {
      const data = d.data();
      const isFromPeer = data.sender_Id !== Number(currentUserId);
      const peerId = isFromPeer
        ? data.sender_Id?.toString()
        : data.receiver_Id?.toString();

      if (!peerId || peerId === currentUserId) return;
      userIds.add(peerId);

      const ts = data.createdAt?.seconds
        ?? (data.createdAt?.toDate ? data.createdAt.toDate().getTime() / 1000 : 0);

      // Latest message per peer
      if (!latestMsg[peerId] || ts > latestMsg[peerId].ts) {
        latestMsg[peerId] = { ts, text: data.text || '', mine: !isFromPeer };
      }

      // Count unread: only messages FROM them, after last-read time
      if (isFromPeer) {
        const lastRead = readTimes[peerId] || 0;
        if (ts > lastRead) {
          unreadCount[peerId] = (unreadCount[peerId] || 0) + 1;
        }
      }
    });

    getUserProfiles(Array.from(userIds)).then((newProfiles) => {
      // Merge new profiles + update all existing users with latest message/unread
      setChatUsers((prev) => {
        const map = {};
        prev.forEach((u) => { map[String(u.id)] = u; });

        // Apply new profiles
        newProfiles.forEach((p) => {
          map[String(p.id)] = {
            ...map[String(p.id)],
            ...p,
            lastMessage: latestMsg[String(p.id)] || map[String(p.id)]?.lastMessage || null,
            unread: unreadCount[String(p.id)] || 0,
          };
        });

        // Also patch every existing entry with latest ts/unread
        Object.keys(map).forEach((id) => {
          const lm = latestMsg[id];
          const uc = unreadCount[id] || 0;
          if (lm) {
            map[id] = { ...map[id], lastMessage: lm, unread: uc };
          }
        });

        return Object.values(map).sort((a, b) => (b.lastMessage?.ts || 0) - (a.lastMessage?.ts || 0));
      });
    });

    // Real-time update for already-loaded users (when no new profiles to fetch)
    setChatUsers((prev) => {
      const next = prev.map((u) => {
        const lm = latestMsg[String(u.id)];
        const uc = unreadCount[String(u.id)] || 0;
        if (!lm) return u;
        return { ...u, lastMessage: lm, unread: uc };
      });
      return next.sort((a, b) => (b.lastMessage?.ts || 0) - (a.lastMessage?.ts || 0));
    });
  };

  useEffect(() => {
    if (!currentUserId) return;
    const messagesRef = collection(db, "messages");
    const q = query(messagesRef, where("participants", "array-contains", Number(currentUserId)));
    const unsubscribe = onSnapshot(q, (snapshot) => processSnapshots(snapshot));
    return () => unsubscribe();
  }, [currentUserId, sidebarVersion]);

  useEffect(() => {
    dispatch(MemberList({ userId: currentUserId }));
  }, []);

  // Focus search when modal opens
  useEffect(() => {
    if (showModal) {
      setTimeout(() => modalInputRef.current?.focus(), 80);
    } else {
      setModalSearch('');
    }
  }, [showModal]);

  const filteredModalUsers = useMemo(() => {
    const term = modalSearch.trim().toLowerCase();
    if (!term) return Userslist;
    return Userslist.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(term) ||
        u.company_name?.toLowerCase().includes(term)
    );
  }, [modalSearch, Userslist]);

  const filteredChatUsers = useMemo(() => {
    const term = chatSearch.trim().toLowerCase();
    if (!term) return ChatUsers;
    return ChatUsers.filter(
      (u) =>
        u.full_name?.toLowerCase().includes(term) ||
        u.company_name?.toLowerCase().includes(term)
    );
  }, [chatSearch, ChatUsers]);

  const handleClick = (data) => {
    markRead(currentUserId, data.id);
    // Clear unread badge in state immediately
    setChatUsers((prev) =>
      prev.map((u) => String(u.id) === String(data.id) ? { ...u, unread: 0 } : u)
    );
    sendData(data);
    setShowModal(false);
  };

  return (
    <>
      <div className="cs-sidebar">

        {/* ── Top header: branding + user ── */}
        <div className="cs-top">
          <div className="cs-top-left">
            <div className="cs-me-avatar-wrap">
              <img
                src={user.member_logo || `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`}
                className="cs-me-avatar"
                alt="me"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`;
                }}
              />
              <span className="cs-presence" />
            </div>
            <div className="cs-me-info">
              <span className="cs-me-name">{user.fullname}</span>
              <span className="cs-me-tag">
                <span className="cs-presence-dot" /> Online
              </span>
            </div>
          </div>
          <div className="cs-top-actions">
            <button className="cs-new-chat-btn" type="button" onClick={() => setShowModal(true)}>
              <i className="bx bx-plus" />
              New Chat
            </button>
          </div>
        </div>

        {/* ── Search bar ── */}
        <div className="cs-search-wrap">
          <i className="bx bx-search cs-search-icon" />
          <input
            className="cs-search-input"
            type="text"
            placeholder="Search recent chats..."
            value={chatSearch}
            onChange={(e) => setChatSearch(e.target.value)}
          />
          {chatSearch && (
            <button className="cs-search-clear" type="button" onClick={() => setChatSearch('')}>
              <i className="bx bx-x" />
            </button>
          )}
        </div>

        {/* ── Divider + label ── */}
        <div className="cs-list-header">
          <span>Messages</span>
          <span className="cs-count">{filteredChatUsers.length}</span>
        </div>

        {/* ── Chat list ── */}
        <div className="cs-list-scroll">
          {filteredChatUsers.length === 0 ? (
            <div className="cs-no-chats">
              <div className="cs-no-chats-icon">
                <i className="bx bx-conversation" />
              </div>
              <p className="cs-no-chats-title">{chatSearch ? 'No results' : 'No messages yet'}</p>
              <p className="cs-no-chats-sub">
                {chatSearch ? `No chats matching "${chatSearch}"` : 'Start a conversation with a member'}
              </p>
              {!chatSearch && (
                <button className="cs-no-chats-btn" type="button" onClick={() => setShowModal(true)}>
                  <i className="bx bx-plus" /> New Chat
                </button>
              )}
            </div>
          ) : (
            filteredChatUsers.map((u) => (
              <div
                className={`cs-item${u.unread ? ' cs-item--unread' : ''}`}
                key={u.id}
                onClick={() => handleClick(u)}
              >
                <div className="cs-item-avatar-wrap">
                  <img
                    src={u.member_logo || `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`}
                    className="cs-item-avatar"
                    alt={u.full_name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`;
                    }}
                  />
                  {u.unread > 0 && <span className="cs-unread-dot" />}
                </div>
                <div className="cs-item-body">
                  <div className="cs-item-row1">
                    <span className={`cs-item-name${u.unread ? ' cs-item-name--bold' : ''}`}>
                      {u.full_name}
                    </span>
                    {u.lastMessage?.ts ? (
                      <span className={`cs-item-time${u.unread ? ' cs-item-time--new' : ''}`}>
                        {new Date(u.lastMessage.ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : null}
                  </div>
                  <div className="cs-item-row2">
                    {u.unread > 1 ? (
                      <span className="cs-item-preview cs-item-preview--new">
                        {u.unread}+ new messages
                      </span>
                    ) : (
                      <span className={`cs-item-preview${u.unread === 1 ? ' cs-item-preview--new' : ''}`}>
                        {u.lastMessage?.text
                          ? (u.lastMessage.mine ? 'You: ' : '') + u.lastMessage.text
                          : u.company_name || ''}
                      </span>
                    )}
                    {u.unread > 0 && (
                      <span className="cs-unread-badge">{u.unread > 9 ? '9+' : u.unread}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── New Chat Modal ── */}
      {showModal && (
        <div className="nc-overlay" onClick={() => setShowModal(false)}>
          <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nc-modal-header">
              <div className="nc-modal-title">
                <div className="nc-modal-title-icon"><i className="bx bx-message-add" /></div>
                <div>
                  <div className="nc-modal-title-text">New Conversation</div>
                  <div className="nc-modal-title-sub">Select a member to chat with</div>
                </div>
              </div>
              <button className="nc-close" type="button" onClick={() => setShowModal(false)}>
                <i className="bx bx-x" />
              </button>
            </div>

            <div className="nc-search-bar">
              <i className="bx bx-search" />
              <input
                ref={modalInputRef}
                type="text"
                placeholder="Search by name or company..."
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
              />
              {modalSearch && (
                <button className="nc-clear" type="button" onClick={() => setModalSearch('')}>
                  <i className="bx bx-x" />
                </button>
              )}
            </div>

            <div className="nc-results-meta">
              {filteredModalUsers.length} member{filteredModalUsers.length !== 1 ? 's' : ''}
            </div>

            <div className="nc-list">
              {filteredModalUsers.length === 0 ? (
                <div className="nc-empty">
                  <i className="bx bx-search-alt" />
                  <p>No members found for "{modalSearch}"</p>
                </div>
              ) : (
                filteredModalUsers.map((u) => (
                  <div className="nc-row" key={u.id} onClick={() => handleClick(u)}>
                    <img
                      src={u.member_logo || `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`}
                      className="nc-row-avatar"
                      alt={u.full_name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `${process.env.PUBLIC_URL}/gray-avatar-icon-vector-illustration_276184-163.avif`;
                      }}
                    />
                    <div className="nc-row-info">
                      <span className="nc-row-name">{u.full_name}</span>
                      <span className="nc-row-company">{u.company_name}</span>
                    </div>
                    <div className="nc-row-action">
                      <i className="bx bx-send" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(ChatSidebar);
